import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

/**
 * Badge/Chip component with color variants and optional removable functionality
 * Supports icons and custom styling
 */
export function Badge({
  children,
  variant = 'gray',
  icon = null,
  onRemove = null,
  className = '',
  ...props
}) {
  const { t } = useTranslation('common');

  // Color variant styles mapping
  const variantStyles = {
    blue: 'bg-blue-100 text-blue-800',
    green: 'bg-green-100 text-green-800',
    red: 'bg-red-100 text-red-800',
    yellow: 'bg-yellow-100 text-yellow-800',
    gray: 'bg-gray-100 text-gray-800',
    purple: 'bg-purple-100 text-purple-800'
  };

  // Combine all styles
  const badgeClassName = `
    ${variantStyles[variant]}
    px-2.5 py-0.5 rounded-full text-xs font-medium inline-flex items-center gap-1
    ${className}
  `.trim().replace(/\s+/g, ' ');

  return (
    <span className={badgeClassName} {...props}>
      {/* Optional icon */}
      {icon && <span className="flex-shrink-0">{icon}</span>}

      {/* Badge content */}
      <span>{children}</span>

      {/* Optional remove button */}
      {onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          className="flex-shrink-0 ml-0.5 inline-flex items-center justify-center w-3.5 h-3.5 rounded-full hover:bg-black hover:bg-opacity-10 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-blue-500"
          aria-label={t('badge.ariaLabels.remove')}
        >
          <RemoveIcon />
        </button>
      )}
    </span>
  );
}

/**
 * Simple X icon for remove button
 */
function RemoveIcon() {
  return (
    <svg
      className="w-2.5 h-2.5"
      fill="currentColor"
      viewBox="0 0 20 20"
      aria-hidden="true"
    >
      <path
        fillRule="evenodd"
        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
        clipRule="evenodd"
      />
    </svg>
  );
}

// PropTypes validation
Badge.propTypes = {
  children: PropTypes.node.isRequired,
  variant: PropTypes.oneOf(['blue', 'green', 'red', 'yellow', 'gray', 'purple']),
  icon: PropTypes.node,
  onRemove: PropTypes.func,
  className: PropTypes.string
};